import { supabase } from '@/integrations/supabase/client';
import { Suggestion } from './guidance';

export interface GuidanceSession {
  id: string;
  user_id: string;
  note_space_id?: string;
  questions: string[];
  current_stage: 'early' | 'middle' | 'late';
  last_suggestions: Suggestion[];
  context_hash: string;
  created_at: string;
  updated_at: string;
}

export class HybridGuidanceManager {
  private sessionId: string | null = null;
  private pulseConnection: WebSocket | null = null;
  private currentContext: {
    questions: string[];
    notes: Array<{ type: string; content: string; created_at?: string }>;
    currentStage: 'early' | 'middle' | 'late';
  } | null = null;

  constructor(private noteSpaceId?: string, private userId?: string) {}

  // Generate a hash for context to detect changes
  private generateContextHash(
    questions: string[],
    notes: Array<{ type: string; content: string; created_at?: string }>,
    stage: string
  ): string {
    const context = {
      questions: questions.sort(),
      notes: notes.map(n => `${n.type}:${n.content}`).sort(),
      stage
    };
    return btoa(JSON.stringify(context));
  }

  // Create or update guidance session in database
  async upsertSession(
    questions: string[],
    notes: Array<{ type: string; content: string; created_at?: string }>,
    currentStage: 'early' | 'middle' | 'late',
    suggestions?: Suggestion[]
  ): Promise<string> {
    if (!this.userId) {
      throw new Error('User ID required for guidance sessions');
    }

    const contextHash = this.generateContextHash(questions, notes, currentStage);
    
    const sessionData = {
      user_id: this.userId,
      note_space_id: this.noteSpaceId,
      questions,
      current_stage: currentStage,
      context_hash: contextHash,
      ...(suggestions && { last_suggestions: suggestions as any })
    };

    if (this.sessionId) {
      // Update existing session
      const { error } = await supabase
        .from('guidance_sessions')
        .update(sessionData)
        .eq('id', this.sessionId);

      if (error) {
        console.error('Failed to update guidance session:', error);
        throw error;
      }
    } else {
      // Create new session
      const { data, error } = await supabase
        .from('guidance_sessions')
        .insert(sessionData)
        .select('id')
        .single();

      if (error) {
        console.error('Failed to create guidance session:', error);
        throw error;
      }

      this.sessionId = data.id;
    }

    return this.sessionId;
  }

  // Get cached suggestions if context hasn't changed
  async getCachedSuggestions(
    questions: string[],
    notes: Array<{ type: string; content: string; created_at?: string }>,
    currentStage: 'early' | 'middle' | 'late'
  ): Promise<Suggestion[] | null> {
    if (!this.sessionId) return null;

    const contextHash = this.generateContextHash(questions, notes, currentStage);
    
    const { data, error } = await supabase
      .from('guidance_sessions')
      .select('last_suggestions, context_hash')
      .eq('id', this.sessionId)
      .single();

    if (error || !data) return null;

    // Return cached suggestions if context hasn't changed
    if (data.context_hash === contextHash && data.last_suggestions) {
      return data.last_suggestions as unknown as Suggestion[];
    }

    return null;
  }

  // Create a pulse connection (short-lived WebSocket)
  async createPulseConnection(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        console.log('Creating pulse WebSocket connection...');
        this.pulseConnection = new WebSocket(
          `wss://iotzzkevshufpjgvxblv.functions.supabase.co/realtime-guidance`
        );

        const timeout = setTimeout(() => {
          console.log('Pulse connection timeout, falling back to HTTP');
          this.disconnectPulse();
          resolve(false);
        }, 5000); // 5 second timeout

        this.pulseConnection.onopen = () => {
          console.log('Pulse connection established');
          clearTimeout(timeout);
          resolve(true);
        };

        this.pulseConnection.onerror = (error) => {
          console.error('Pulse connection error:', error);
          clearTimeout(timeout);
          this.disconnectPulse();
          resolve(false);
        };

        this.pulseConnection.onclose = () => {
          console.log('Pulse connection closed');
          this.pulseConnection = null;
        };

      } catch (error) {
        console.error('Failed to create pulse connection:', error);
        resolve(false);
      }
    });
  }

  // Disconnect pulse connection
  disconnectPulse(): void {
    if (this.pulseConnection) {
      this.pulseConnection.close();
      this.pulseConnection = null;
    }
  }

  // Send guidance request via pulse connection
  async requestViaWebSocket(
    questions: string[],
    notes: Array<{ type: string; content: string; created_at?: string }>,
    currentStage: 'early' | 'middle' | 'late'
  ): Promise<Suggestion[]> {
    if (!this.pulseConnection || this.pulseConnection.readyState !== WebSocket.OPEN) {
      throw new Error('No active WebSocket connection');
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        console.log('WebSocket request timeout');
        this.disconnectPulse();
        reject(new Error('Request timeout'));
      }, 30000); // 30 second timeout

      const handleMessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'guidance_complete') {
            clearTimeout(timeout);
            this.pulseConnection?.removeEventListener('message', handleMessage);
            this.disconnectPulse(); // Close connection after receiving response
            resolve(data.suggestions || []);
          } else if (data.type === 'error') {
            clearTimeout(timeout);
            this.pulseConnection?.removeEventListener('message', handleMessage);
            this.disconnectPulse();
            reject(new Error(data.message));
          }
        } catch (error) {
          console.error('Error parsing WebSocket response:', error);
          clearTimeout(timeout);
          this.disconnectPulse();
          reject(error);
        }
      };

      this.pulseConnection.addEventListener('message', handleMessage);

      // Send the request
      this.pulseConnection.send(JSON.stringify({
        type: 'request_guidance',
        questions,
        notes,
        currentStage
      }));
    });
  }

  // Main method to get guidance using hybrid approach
  async getGuidance(
    questions: string[],
    notes: Array<{ type: string; content: string; created_at?: string }>,
    currentStage: 'early' | 'middle' | 'late' = 'middle',
    onStreamingUpdate?: (content: string) => void
  ): Promise<Suggestion[]> {
    this.currentContext = { questions, notes, currentStage };

    // Step 1: Check for cached suggestions
    const cached = await this.getCachedSuggestions(questions, notes, currentStage);
    if (cached && cached.length > 0) {
      console.log('Using cached suggestions');
      return cached;
    }

    // Step 2: Try pulse WebSocket connection
    const pulseConnected = await this.createPulseConnection();
    
    if (pulseConnected) {
      try {
        console.log('Attempting guidance via WebSocket pulse...');
        const suggestions = await this.requestViaWebSocket(questions, notes, currentStage);
        
        // Cache the results
        await this.upsertSession(questions, notes, currentStage, suggestions);
        
        return suggestions;
      } catch (error) {
        console.error('WebSocket pulse failed, falling back to HTTP:', error);
        this.disconnectPulse();
      }
    }

    // Step 3: Fallback to HTTP
    console.log('Using HTTP fallback for guidance...');
    const { getLiveGuidance } = await import('./guidance');
    const suggestions = await getLiveGuidance(questions, notes, currentStage);
    
    // Cache the results
    await this.upsertSession(questions, notes, currentStage, suggestions);
    
    return suggestions;
  }

  // Clean up resources
  cleanup(): void {
    this.disconnectPulse();
    this.sessionId = null;
    this.currentContext = null;
  }
}